<script setup>
import { IMaskComponent } from "vue-imask";
import { computed, ref } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

const emit = defineEmits(["update-size", "track-custom-placement", "input-touched"]);
const props = defineProps({
  artworkSize: {
    type: Object,
    required: true
  },
  surface: {
    type: Object,
    required: true
  },
  downscaleFactor: {
    type: Number,
    required: true
  },
  pxToInch: {
    type: Function,
    required: true
  },
  inchToPx: {
    type: Function,
    required: true
  }
});

const setSizes = ref({ width: 0, height: 0 });

const boundingBoxSize = computed(() => ({
  width: props.surface.bounding_box.width * props.downscaleFactor,
  height: props.surface.bounding_box.height * props.downscaleFactor
}));

const artworkWidth = computed(() => props.pxToInch(props.artworkSize.width));
const artworkHeight = computed(() => props.pxToInch(props.artworkSize.height));
const maxInchSize = size => (props.surface.bounding_box[size] / props.surface.ppi).toFixed(2);

const handleChangeSize = (size, side) => {
  emit("input-touched");
  setSizes.value = { ...setSizes.value, [side]: size };
  const validatedValue = parseFloat(size) || boundingBoxSize.value[side];
  const minValue = Math.max(validatedValue, 1);
  const value = Math.min(props.inchToPx(minValue), boundingBoxSize.value[side]);
  const ratio = value / props.artworkSize[side];
  const oppositeSide = side === "width" ? "height" : "width";
  const oppositeSize = ratio * props.artworkSize[oppositeSide];
  if (oppositeSize <= boundingBoxSize.value[oppositeSide] && props.pxToInch(oppositeSize) >= 1) {
    emit("update-size", { [side]: value, [oppositeSide]: oppositeSize });
    emit("track-custom-placement", "resized");
  } else if (props.pxToInch(oppositeSize) < 1) {
    handleChangeSize(1, oppositeSide);
  } else if (oppositeSize > boundingBoxSize.value[oppositeSide]) {
    handleChangeSize(props.pxToInch(boundingBoxSize.value[oppositeSide]), oppositeSide);
  }
};
</script>

<template>
  <div class="artwork-size">
    <div class="artwork-size__fields-wrapper">
      <div class="artwork-size__field-wrapper artwork-size__field-wrapper--width">
        <IMaskComponent
          :key="setSizes.width"
          :mask="'num”'"
          :lazy="false"
          :blocks="{ num: { mask: Number, radix: '.' } }"
          class="artwork-size__field"
          :value="`${artworkWidth}”`"
          data-testid="artwork-size-width"
          @keypress.enter.prevent="handleChangeSize($event.target.value, 'width')"
          @change="handleChangeSize($event.target.value, 'width')"
        />
        <div class="artwork-size__field-label">Width</div>
      </div>
      <div class="artwork-size__cross-wrapper">
        <UseSvg icon="cross" />
      </div>
      <div class="artwork-size__field-wrapper artwork-size__field-wrapper--height">
        <IMaskComponent
          :key="setSizes.height"
          :mask="'num”'"
          :lazy="false"
          :blocks="{ num: { mask: Number, radix: '.' } }"
          class="artwork-size__field"
          :value="`${artworkHeight}”`"
          data-testid="artwork-size-height"
          @keypress.enter.prevent="handleChangeSize($event.target.value, 'height')"
          @change="handleChangeSize($event.target.value, 'height')"
        />
        <div class="artwork-size__field-label">Height</div>
      </div>
    </div>

    <div class="artwork-size__max-size-info">
      The maximum size for artwork is {{ maxInchSize("width") }}” x {{ maxInchSize("height") }}”
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";

.artwork-size {
  &__fields-wrapper {
    display: grid;
    grid-column-gap: 6px;
    grid-template-areas: "width cross height";
    grid-template-columns: 1fr 18px 1fr;

    &__field-wrapper {
      &--width {
        grid-area: width;
      }

      &--height {
        grid-area: height;
      }
    }
  }

  &__cross-wrapper {
    @include svg-wrapper;

    width: 18px;
    height: 18px;
    margin-top: 11px;
    padding: 5px;
    grid-area: cross;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $pod-gray-modern-100;
    color: $pod-gray-modern-900;
    border-radius: 50%;
  }

  &__field {
    display: block;
    width: 100%;
    height: 40px;
    color: $pod-gray-modern-900;
    border: 2px solid $pod-gray-modern-200;
    border-radius: 4px;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
  }

  &__field-label {
    margin-top: 1px;
    text-align: center;
    font-size: 11px;
    line-height: 1.2;
    font-weight: 500;
    color: $pod-gray-modern-500;
  }

  &__max-size-info {
    text-align: center;
    font-size: 11px;
    color: $pod-gray-modern-500;
    font-weight: bold;
    margin-top: 10px;
    height: 17px;
  }
}
</style>
